import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

const api = clientFor('/wms/api')

export type WaveStatus = 'open' | 'picking' | 'picked' | 'closed' | 'cancelled'

/** 拣货任务状态，与出库单里的 pick_tasks 同一套字典 */
export type PickTaskStatus = 'pending' | 'done' | 'cancelled'

export interface Wave {
  id: number
  tenant_id: number
  doc_no: string
  warehouse_id: number
  status: WaveStatus
  remark: string
  created_by: string
  created_at: string
  updated_at: string
}

/** 列表行：波次 + 仓库名 + 任务数/完成数/包含的出库单数 */
export interface ListRow extends Wave {
  warehouse_name: string
  task_count: number
  done_count: number
  order_count: number
}

/** 拣货任务 + 商品、来源库位与所属出库单的展示信息 */
export interface TaskRow {
  id: number
  tenant_id: number
  task_no: string
  wave_id: number
  order_id: number
  order_item_id: number
  sku_id: number
  batch_no: string
  qty: number
  from_location_id: number
  status: PickTaskStatus
  created_at: string
  updated_at: string
  sku_code: string
  sku_name: string
  unit: string
  from_location_code: string
  order_doc_no: string
  order_status: string
}

export interface OrderRef {
  id: number
  doc_no: string
  status: string
}

export interface Detail {
  wave: Wave
  warehouse_name: string
  tasks: TaskRow[]
  orders: OrderRef[]
}

export interface CreateInput {
  order_ids: number[]
  remark?: string
}

export interface WaveQuery extends PageQuery {
  keyword?: string
  warehouse?: number
  status?: string
}

/** 与后端 services/wms/internal/wave/model.go 的字典保持一致，改一边必须改另一边 */
export const STATUS_LABELS: Record<WaveStatus, string> = {
  open: '待拣货',
  picking: '拣货中',
  picked: '已拣货',
  closed: '已关闭',
  cancelled: '已取消',
}

/** 与后端 services/wms/internal/outbound/model.go 的 TaskNames 一致 */
export const TASK_STATUS_LABELS: Record<PickTaskStatus, string> = {
  pending: '待拣货',
  done: '已完成',
  cancelled: '已取消',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<WaveStatus, 'info' | 'warning' | 'primary' | 'success' | 'danger'> = {
  open: 'primary',
  picking: 'warning',
  picked: 'success',
  closed: 'info',
  cancelled: 'danger',
}

export const listWaves = (q: WaveQuery): Promise<PageResult<ListRow>> =>
  api.get<PageResult<ListRow>>('/waves', { ...q })

export const getWave = (id: number): Promise<Detail> => api.get<Detail>(`/waves/${id}`)

export const createWave = (input: CreateInput): Promise<Wave> => api.post<Wave>('/waves', input)

export const cancelWave = (id: number): Promise<void> =>
  api.post<void>(`/waves/${id}/cancel`, {})

export const completeWave = (id: number): Promise<Detail> =>
  api.post<Detail>(`/waves/${id}/complete`, {})

export const donePickTask = (taskId: number): Promise<void> =>
  api.post<void>(`/pick-tasks/${taskId}/done`, {})
